import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface KlingAvatarParams {
  /**
   * 数字人图片,支持URL或Base64格式
   */
  image: string;
  /**
   * 音频ID,与sound_file二选一。不能同时提供audio_id和sound_file
   */
  audio_id?: string;
  /**
   * 音频文件URL或Base64,与audio_id二选一。不能同时提供audio_id和sound_file
   */
  sound_file?: string;
  /**
   * 文本提示词,可选参数
   */
  prompt?: string;
  /**
   * 生成模式
   * @default "std"
   * @values std | pro
   */
  mode?: 'std' | 'pro';
  /**
   * 外部任务ID,用于关联外部系统
   */
  external_task_id?: string;
}

export interface KlingAvatarResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_avatar
 *
 * API Documentation: https://app.klingai.com/cn/dev/document-api/apiReference/model/avatar
 * 
 * Kling AI 数字人视频生成接口,支持通过图片和音频生成数字人视频。
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingAvatar } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingAvatar({
 *   // 参数见 KlingAvatarParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingAvatarParams 接口定义
 * @returns 生成的资源列表
 */
export async function klingAvatar(
  params: KlingAvatarParams,
): Promise<KlingAvatarResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_avatar',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingAvatarResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
